import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { twMerge } from 'tailwind-merge';

import {
  Chip,
  BaseButton,
  CloseButton,
  Input,
  Checkbox,
  Toast,
  useScrollLock,
  handleListContinuation,
} from '@repo/common';

import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-top.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-bottom.svg';

import { getQnaCategories } from '../../../api/qna/qnaCategory.api';
import { postNewQuestion, updateQuestion } from '../../../api/qna/qna.api';

const FakePlaceholder = ({ children, className }) => (
  <span
    className={twMerge(
      'absolute z-50 text-text-placeholder pointer-events-none',
      className,
    )}
  >
    {children}
  </span>
);

const AskQuestionModal = ({ isOpen, onClose, initialData }) => {
  const navigate = useNavigate();
  const isInitialized = useRef(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(
    initialData?.categoryId || null,
  );
  const [title, setTitle] = useState(initialData?.title || '');
  const [content, setContent] = useState(initialData?.content || '');
  const [isVisibleToTutorOnly, setIsVisibleToTutorOnly] = useState(
    initialData?.tutorOnly || false,
  );
  const [failedToastVisible, setFailedToastVisible] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const categoryRef = useRef(null);

  useScrollLock(isOpen);

  /*
   * 카테고리 드롭다운 바깥을 누르면 닫는다.
   * pointerdown 을 쓰는 이유 — click 으로 잡으면 모바일에서 항목을 고르는 탭이
   * 바깥 판정과 겹쳐 선택이 씹힐 수 있다. 캡처 단계에서 ref 포함 여부만 본다.
   */
  useEffect(() => {
    if (!isCategoryOpen) return undefined;

    const handlePointerDown = (event) => {
      if (categoryRef.current?.contains(event.target)) return;
      setIsCategoryOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isCategoryOpen]);

  useEffect(() => {
    const fetchCategories = async () => {
      const data = await getQnaCategories({ includeAll: false });
      setCategories(data);
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    if (!isInitialized.current) {
      if (initialData) {
        setTitle(initialData.title || '');
        setContent(initialData.content || '');
        setSelectedCategory(initialData.categoryId || null);
        setIsVisibleToTutorOnly(initialData.tutorOnly || false);
      } else {
        setTitle('');
        setContent('');
        setSelectedCategory(null);
        setIsVisibleToTutorOnly(false);
      }
      isInitialized.current = true;
    }
  }, [initialData, isOpen]);

  if (!isOpen) return;

  const handleAskClick = async () => {
    const data = {
      categoryId: selectedCategory,
      title,
      content,
      tutorOnly: isVisibleToTutorOnly,
    };
    try {
      let response;
      if (initialData?.id) {
        response = await updateQuestion(initialData.id, data);
      } else {
        response = await postNewQuestion(data);
      }

      const newQnaId = response.id;

      onClose();
      setSelectedCategory(null);
      setTitle('');
      setContent('');
      setIsVisibleToTutorOnly(false);

      navigate(`/community/qna/${newQnaId}`, {
        state: { showSuccessToast: true, newQnaId },
      });
    } catch (error) {
      console.error('Q&A 등록/수정 실패:', error);
      setFailedToastVisible(false);
      setTimeout(() => setFailedToastVisible(true), 3);
    }
  };

  const selectedCategoryName =
    categories.find((c) => c.id === selectedCategory)?.name ||
    'Select category';

  return ReactDOM.createPortal(
    <div
      className='fixed inset-0 z-[1000000000] flex items-end tabletH:items-center justify-center bg-surface-black40'
      onClick={onClose}
    >
      {/* 모달 컨테이너 */}
      <div
        className={twMerge(
          'relative w-full h-full bg-grayscale-gray00 tabletH:bg-grayscale-gray01 animate-fadeIn',
          'tabletH:h-auto tabletH:max-w-[840px] tabletH:rounded-[40px]',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 영역 */}
        <div className='flex justify-between items-center px-4 py-[6px] tabletH:px-6 tabletH:pt-5 tabletH:pb-4'>
          <div className='w-10 tabletH:w-[120px]'>
            <div className='tabletH:hidden'>
              <CloseButton
                type='default'
                onClick={onClose}
                bgClass={'bg-grayscale-gray00'}
                size='w-12 h-12'
              />
            </div>
            <div className='hidden tabletH:block'>
              <BaseButton
                onClick={() => {
                  onClose();
                }}
                type='text'
                size='xl'
                hideIcon
                textclass='text-green-deep03'
              >
                close
              </BaseButton>
            </div>
          </div>

          <div className='typo-title2 text-text-primary'>
            {initialData ? 'Edit Question' : 'New Question'}
          </div>

          <div className='w-10 tabletH:w-[120px] flex justify-center'>
            <button
              disabled={!title.trim() || !selectedCategory}
              onClick={handleAskClick}
              className={twMerge(
                'tabletH:hidden typo-b16Strong',
                !title.trim() || !selectedCategory
                  ? 'text-grayscale-gray04'
                  : 'text-green-deep03',
              )}
            >
              Ask
            </button>
            <div className='hidden tabletH:block'>
              <BaseButton
                size='l'
                isDisabled={!title.trim() || !selectedCategory}
                hideIcon
                width='120px'
                onClick={handleAskClick}
              >
                Ask
              </BaseButton>
            </div>
          </div>
        </div>

        {/* 카테고리 선택 섹션 */}
        <div className='pb-[100px] tabletH:pb-0'>
          <div className='px-4 pt-4 pb-[36px] tabletH:px-10 tabletH:pt-0 tabletH:pb-[30px]'>
            <div className='block px-2 typo-b16Strong text-grayscale-gray09 mb-2'>
              category
              <span className='typo-title3 text-functional-accent03 ml-[2px]'>
                *
              </span>
            </div>

            {/* 모바일 */}
            <div
              ref={categoryRef}
              className='relative tabletH:hidden mb-4 z-[9999999]'
            >
              <button
                type='button'
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                className={twMerge(
                  'w-full h-12 px-4 flex items-center justify-between border rounded-[8px]',
                  isCategoryOpen
                    ? 'border-grayscale-gray07'
                    : 'border-grayscale-gray03',
                )}
              >
                <span className='text-text-primary'>
                  {selectedCategoryName}
                </span>
                {isCategoryOpen ? <ArrowTopIcon /> : <ArrowBottomIcon />}
              </button>

              {isCategoryOpen && (
                <div className='absolute top-14 left-0 w-full bg-grayscale-gray00 border border-grayscale-gray02 rounded-[8px] shadow-lg z-50 py-2'>
                  {categories.map((category) => (
                    <BaseButton
                      key={category.id}
                      type='text'
                      hideIcon
                      leftalign='true'
                      width='100%'
                      bgclass='bg-grayscale-gray00 ring-0'
                      activeclass='active:text-text-primary active:bg-grayscale-gray01'
                      onClick={() => {
                        setSelectedCategory(category.id);
                        setIsCategoryOpen(false);
                      }}
                    >
                      {category.name}
                    </BaseButton>
                  ))}
                </div>
              )}
            </div>

            {/* 데스크탑 */}
            <div className='hidden tabletH:flex flex-wrap gap-2 pb-4'>
              {categories.map((category) => {
                const isActive = selectedCategory === category.id;
                return (
                  <Chip
                    key={category.id}
                    type={isActive ? 'fill' : 'outline'}
                    state={isActive ? 'selected' : 'default'}
                    hideIcon={true}
                    onClick={() => setSelectedCategory(category.id)}
                    disableTypography
                    borderclass='ring-0'
                    bgclass={`
                      ${isActive ? '' : 'bg-grayscale-gray02 text-grayscale-gray07'}
                      px-4 py-3 h-11
                    `}
                    textclass='typo-b14Strong'
                  >
                    {category.name}
                  </Chip>
                );
              })}
            </div>

            {/* 모바일 Tutor Only */}
            <div className='mt-4 pb-4 tabletH:hidden'>
              <Checkbox
                checked={isVisibleToTutorOnly}
                onChange={() => setIsVisibleToTutorOnly(!isVisibleToTutorOnly)}
                label='Only with tutor’s answer'
                className={`${isVisibleToTutorOnly ? 'text-text-primary' : ''}`}
                boxClassName={
                  isVisibleToTutorOnly
                    ? 'bg-functional-success03 border-none'
                    : 'bg-grayscale-gray00 border border-grayscale-gray03'
                }
              />
            </div>

            {/* 질문 입력 섹션 */}
            <div className='py-2 tabletH:p-0 space-y-4'>
              <div className='relative'>
                <Input
                  id='question-title-input'
                  label=''
                  placeholder=''
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                {!title && (
                  <FakePlaceholder className={'absolute left-5 top-3'}>
                    Got a new question?
                    <span className='text-functional-accent03 ml-[2px]'>*</span>
                  </FakePlaceholder>
                )}
              </div>

              <div className='relative'>
                <Input
                  as='textarea'
                  label=''
                  placeholder=''
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  // "- " / "1. " 로 시작한 줄에서 Enter 시 다음 항목을 이어 붙인다
                  onKeyDown={(e) => handleListContinuation(e, setContent, 500)}
                  maxLength={500}
                  textareaMinHeight='120px'
                  textareaRows={10}
                />
                {!content && (
                  <FakePlaceholder className={'left-5 top-[14px]'}>
                    Add details (optional)
                  </FakePlaceholder>
                )}
              </div>
            </div>

            {/* 데스크탑 Tutor Only */}
            <div className='mt-4 hidden tabletH:block'>
              <Checkbox
                checked={isVisibleToTutorOnly}
                onChange={() => setIsVisibleToTutorOnly(!isVisibleToTutorOnly)}
                label='Visible to tutor only'
                className={`${isVisibleToTutorOnly ? 'text-text-primary' : ''}`}
                boxClassName={
                  isVisibleToTutorOnly
                    ? 'bg-functional-success03 border-none'
                    : 'bg-grayscale-gray00 border border-grayscale-gray03'
                }
              />
            </div>

            {failedToastVisible && (
              <div>
                <div className='hidden tabletH:fixed bottom-[200px] left-1/2 -translate-x-1/2 z-[1000]'>
                  <Toast
                    visibleOn='desktop'
                    hierarchy='important'
                    type='error'
                    state='white'
                    title='Something went wrong'
                    message='Please Try again'
                    direction='up'
                    customWidth={'auto'}
                  />
                </div>
                <div className='fixed tabletH:hidden left-1/2 -translate-x-1/2 bottom-[280px] z-[1000]'>
                  <Toast
                    visibleOn='mobile'
                    hierarchy='important'
                    type='error'
                    state='white'
                    title='Something went wrong'
                    message='Please Try again'
                    direction='up'
                    customWidth={'280px'}
                  />
                </div>
              </div>
            )}
          </div>
          <div className='tabletH:hidden px-4'>
            <BaseButton
              size='l'
              isDisabled={!title.trim() || !selectedCategory}
              hideIcon
              width='100%'
              onClick={handleAskClick}
            >
              Ask
            </BaseButton>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default AskQuestionModal;
